import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import type { KuboMood } from '../components/ui/kubo-avatar.component';
import { AuthService } from './auth.service';
import { DashboardService } from './dashboard.service';
import { Novelty, NoveltyService } from './novelty.service';
import { RouteLoadingService } from './route-loading.service';
import { KuboTarget, clearKuboGuide, guideKuboTo } from './kubo-guide';

export type { KuboTarget } from './kubo-guide';

export type KuboState = 'default' | 'thinking' | 'recommendation' | 'warning' | 'success';
export type KuboPriority = 'low' | 'medium' | 'high' | 'critical';
export type KuboTone = 'info' | 'success' | 'warning' | 'danger';

export interface KuboRecommendation {
  id: string;
  state: KuboState;
  title: string;
  message: string;
  actionLabel?: string;
  priority?: KuboPriority;
  onAction?: () => void;
  // Segunda acción opcional (p. ej. "Omitir recorrido" junto a "Iniciar ahora").
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  // Indicador de "procesando" (Kubo pasa a su estado de carga).
  loading?: boolean;
  // Frases cortas que rotan en el globo mientras la tarjeta está cerrada
  // (recordatorios de fondo: "Tienes cosas por completar"...).
  bubbles?: string[];
  /** Expresión explícita; si falta se deduce de `state`. */
  mood?: KuboMood;
}

/** Mensaje que Kubo dice en su globo (avisos, reacciones, novedades). */
export interface KuboMessage {
  id: number;
  title: string;
  text: string;
  tone: KuboTone;
  mood: KuboMood;
  action?: { label: string; run: () => void } | null;
  /** Aviso que al cerrarse queda descartado en la sesión. */
  alertKey?: string;
  /** Segundos visible (0 = hasta que se cierre). */
  ttl: number;
}

export interface KuboProgressStep {
  key: string;
  title: string;
  description: string;
  done: boolean;
  go?: () => void;
}

export interface KuboProgress {
  title: string;
  steps: KuboProgressStep[];
}

/** Aviso importante del panel (pagos, solicitudes, espacios sin foto...). */
export interface KuboAlert {
  key: string;
  tone: KuboTone;
  mood: KuboMood;
  icon: string;
  title: string;
  message: string;
  actionLabel?: string;
  module?: string;
  /** Lugar exacto del módulo donde se resuelve (Kubo lo señala al llegar). */
  target?: KuboTarget;
  /** Importante de verdad: vuelve a recordarse aunque se haya cerrado. */
  insist?: boolean;
}

const PRIORITY_WEIGHT: Record<KuboPriority, number> = { critical: 3, high: 2, medium: 1, low: 0 };
const STATE_MOOD: Record<KuboState, KuboMood> = { default: 'saludo', thinking: 'pensando', recommendation: 'explicando', warning: 'advertencia', success: 'exito' };
const COMPACT_KEY = 'tusala_kubo_compact';
/** Avisos importantes: se vuelven a recordar cada 8 minutos (al cambiar de módulo). */
const INSIST_EVERY_MS = 8 * 60_000;
/** Las novedades se consultan como máximo cada 20 s (solo por navegación o al guardar: sin sondeo). */
const NOVELTY_MIN_GAP_MS = 20_000;

export function moodForRecommendation(rec: KuboRecommendation | null): KuboMood {
  if (!rec) return 'reposo';
  if (rec.loading) return 'carga';
  return rec.mood ?? STATE_MOOD[rec.state] ?? 'saludo';
}

/**
 * Cerebro de Kubo (mismo enfoque que Lumi en el sistema de enfermeras): los
 * datos vienen de los servicios reales del panel y aquí se decide qué dice y
 * cómo reacciona.
 * - Guardar → "pensando"; guardó → "éxito"; error → "advertencia".
 * - Novedades sin leer y avisos importantes: los anuncia una vez por sesión
 *   (los importantes vuelven a recordarse cada 8 minutos).
 * - Los módulos (onboarding, estadísticas...) le pasan recomendaciones ya
 *   resueltas; Kubo muestra la de mayor prioridad.
 * Sin sondeos ni temporizadores de fondo: todo se recalcula por navegación,
 * al guardar o al volver a la pestaña.
 */
@Injectable({ providedIn: 'root' })
export class KuboService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly novelty = inject(NoveltyService);
  private readonly dashboard = inject(DashboardService);
  private readonly routeLoading = inject(RouteLoadingService);

  /** Hay un Kubo global (dock) montado: las instancias locales le delegan. */
  readonly dockMounted = signal(false);
  /** Oculto mientras otra pantalla de Kubo ocupa la escena (bienvenida, tour). */
  readonly suppressed = signal(false);
  readonly panelOpen = signal(false);
  readonly compact = signal(this.read(COMPACT_KEY) === '1');
  readonly message = signal<KuboMessage | null>(null);
  readonly phrase = signal<string | null>(null);
  readonly progress = signal<KuboProgress | null>(null);
  readonly novelties = signal<Novelty[]>([]);
  private readonly reaction = signal<KuboMood | null>(null);
  private readonly sources = signal<Record<string, KuboRecommendation[]>>({});
  private readonly dismissedAlerts = signal<ReadonlySet<string>>(new Set());

  readonly recommendations = computed(() => Object.values(this.sources()).flat());
  readonly active = computed<KuboRecommendation | null>(() => {
    const list = this.recommendations();
    if (!list.length) return null;
    return [...list].sort((a, b) => PRIORITY_WEIGHT[b.priority || 'medium'] - PRIORITY_WEIGHT[a.priority || 'medium'])[0];
  });

  readonly role = computed(() => (this.auth.currentUser() as any)?.role as string | undefined);
  readonly isSuperAdmin = computed(() => this.role() === 'super_admin');
  readonly firstName = computed(() => {
    const u = this.auth.currentUser() as any;
    return String(u?.session_user?.name || u?.owner?.name || '').trim().split(/\s+/)[0] || '';
  });
  readonly unread = computed(() => (this.isSuperAdmin() ? this.novelty.globalUnread() : this.novelty.businessUnread()));

  /** Avisos importantes derivados de los datos reales del negocio. */
  readonly alerts = computed<KuboAlert[]>(() => {
    const dismissed = this.dismissedAlerts();
    const list: KuboAlert[] = [];
    const canSpaces = this.auth.hasPermission('spaces');
    for (const task of this.dashboard.tasks()) {
      if (task.key === 'payments') list.push({ key: 'payments', tone: 'warning', mood: 'advertencia', icon: 'bi-cash-coin', title: task.title, message: 'Confírmalos para que tus clientes reciban su comprobante a tiempo.', actionLabel: 'Ver reservas', module: 'reservas', insist: true });
      if (task.key === 'requests') list.push({ key: 'requests', tone: 'warning', mood: 'pregunta', icon: 'bi-inbox', title: task.title, message: 'Hay clientes esperando tu respuesta.', actionLabel: 'Revisar solicitudes', module: 'reservas', insist: true });
      if (task.key === 'spaces' && canSpaces) list.push({ key: 'spaces-photos', tone: 'info', mood: 'explicando', icon: 'bi-lightbulb', title: 'Te recomiendo agregar fotos de tu espacio', message: `${task.title}: con portada y tarifa recibirás más reservas.`, actionLabel: 'Ver consejos', module: 'salas', target: { selector: '.espacio-card:has(.no-image), .espacio-card', label: 'Agrega fotos a este espacio' } });
    }
    if (canSpaces && (this.dashboard.loaded() || this.dashboard.summaryLoaded()) && !this.dashboard.spacesLoading() && !this.dashboard.spacesError() && this.dashboard.spaces().length === 0) {
      list.unshift({ key: 'no-spaces', tone: 'info', mood: 'explicando', icon: 'bi-door-open', title: 'Aún no tienes espacios', message: 'Publica tu primer espacio y comienza a recibir reservas.', actionLabel: 'Publicar ahora', module: 'salas', target: { selector: '.spaces-heading-actions .btn-primary', label: 'Crea tu primer espacio aquí' }, insist: true });
    }
    return list.filter(a => !dismissed.has(a.key));
  });

  readonly pendingSteps = computed(() => (this.progress()?.steps ?? []).filter(s => !s.done).length);
  readonly badge = computed(() => this.unread() + this.pendingSteps() + this.alerts().filter(a => a.tone === 'warning' || a.tone === 'danger').length);

  /** Expresión actual: reacción del momento > lo que está diciendo > reposo según la situación. */
  readonly mood = computed<KuboMood>(() => {
    const reaction = this.reaction();
    if (reaction) return reaction;
    if (this.routeLoading.visible()) return 'carga';
    const message = this.message();
    if (message) return message.mood;
    const rec = this.active();
    if (rec) return moodForRecommendation(rec);
    if (this.alerts().some(a => a.tone === 'danger')) return 'advertencia';
    return 'reposo';
  });

  private nextId = 1;
  private reactionTimer?: ReturnType<typeof setTimeout>;
  private phraseTimer?: ReturnType<typeof setTimeout>;
  private messageTimer?: ReturnType<typeof setTimeout>;
  private savingTimer?: ReturnType<typeof setTimeout>;
  private uid: number | null = null;
  private greeted = false;
  private lastNoveltyLoad = 0;
  private summaryAt = 0;
  private seenNovelties = new Set<number>();
  private noveltiesLoadedOnce = false;
  private readonly shownAlerts = new Map<string, number>();
  private lastErrorAt = 0;
  private pendingWrites = 0;

  // ------------------------------------------------------------ recomendaciones
  setRecommendations(source: string, list: KuboRecommendation[]): void {
    this.sources.update(all => ({ ...all, [source]: list || [] }));
  }

  clearRecommendations(source: string): void {
    this.sources.update(all => {
      if (!(source in all)) return all;
      const next = { ...all };
      delete next[source];
      return next;
    });
  }

  // ------------------------------------------------------------ hablar y reaccionar
  say(msg: Omit<KuboMessage, 'id'>): void {
    if (this.messageTimer) clearTimeout(this.messageTimer);
    const item = { ...msg, id: this.nextId++ };
    this.message.set(item);
    if (item.ttl > 0) this.messageTimer = setTimeout(() => { if (this.message()?.id === item.id) this.message.set(null); }, item.ttl * 1000);
  }

  dismissMessage(): void {
    const current = this.message();
    this.message.set(null);
    if (current?.alertKey) this.shownAlerts.set(current.alertKey, Date.now());
  }

  /** Cambia la expresión un momento y luego vuelve a la de reposo. */
  react(mood: KuboMood, ms = 2400): void {
    if (this.reactionTimer) clearTimeout(this.reactionTimer);
    this.reaction.set(mood);
    if (ms > 0) this.reactionTimer = setTimeout(() => this.reaction.set(null), ms);
  }

  /** Frase corta en el globo pequeño ("¡Listo!", "Un momento..."). */
  quick(text: string, ms = 2600): void {
    if (this.phraseTimer) clearTimeout(this.phraseTimer);
    this.phrase.set(text);
    this.phraseTimer = setTimeout(() => this.phrase.set(null), ms);
  }

  // ------------------------------------------------------------ reacciones a lo que se guarda
  saving(): void {
    this.pendingWrites++;
    this.react('pensando', 0);
    if (this.savingTimer) clearTimeout(this.savingTimer);
    // Si tarda, pasa a "Un momento..." con el anillo de carga.
    this.savingTimer = setTimeout(() => { if (this.pendingWrites > 0) { this.react('carga', 0); this.quick('Un momento...', 8000); } }, 900);
  }

  saved(): void {
    this.finishWrite();
    this.react('exito', 2200);
    if (!this.message()) this.quick('¡Listo!', 1800);
    // Algo pudo cambiar (un espacio nuevo, una reserva confirmada...). Una
    // racha de guardados seguidos produce UNA sola actualización al final.
    if (this.savedRefreshTimer) clearTimeout(this.savedRefreshTimer);
    this.savedRefreshTimer = setTimeout(() => { this.savedRefreshTimer = undefined; this.refresh(true); }, 1500);
  }
  private savedRefreshTimer?: ReturnType<typeof setTimeout>;

  failed(): void {
    this.finishWrite();
    if (Date.now() - this.lastErrorAt < 1200) return;
    this.lastErrorAt = Date.now();
    this.react('advertencia', 4200);
    this.quick('Revisa este detalle', 4200);
  }

  /** La conexión falló (no es un error de quien usa el panel). */
  offline(): void {
    this.finishWrite();
    this.react('triste', 4200);
    this.quick('Lo siento... no pude conectar', 4200);
  }

  cancelled(): void { this.finishWrite(); }

  private finishWrite(): void {
    this.pendingWrites = Math.max(0, this.pendingWrites - 1);
    if (this.savingTimer) clearTimeout(this.savingTimer);
    if (this.phrase() === 'Un momento...') this.phrase.set(null);
    if (!this.pendingWrites) this.reaction.set(null);
  }

  // ------------------------------------------------------------ panel
  togglePanel(open?: boolean): void { this.panelOpen.set(open ?? !this.panelOpen()); }

  toggleCompact(): void {
    this.compact.update(v => !v);
    this.write(COMPACT_KEY, this.compact() ? '1' : '0');
  }

  /** Lleva al módulo y, si se indica, señala el lugar exacto donde completar. */
  goModule(module: string, target?: KuboTarget, explain?: { title: string; text: string }): void {
    this.panelOpen.set(false);
    void this.router.navigate(['/dashboard'], { queryParams: { module } });
    if (target) this.guide(target, explain);
  }

  /**
   * Señala un elemento de la pantalla (lo centra, lo resalta y le pone una
   * etiqueta "Completa aquí") y explica en el globo qué hacer ahí.
   */
  guide(target: KuboTarget, explain?: { title: string; text: string }): void {
    this.react('explicando', 3500);
    void guideKuboTo(target).then(found => {
      if (found && explain) this.say({ title: explain.title, text: explain.text, tone: 'info', mood: 'explicando', ttl: 9 });
    });
  }

  openNovelties(): void { this.goModule(this.isSuperAdmin() ? 'superadminNovedades' : 'novedades'); }

  startTutorial(): void {
    this.panelOpen.set(false);
    window.dispatchEvent(new CustomEvent('tusala:start-tutorial'));
  }

  dismissAlert(key: string): void {
    this.dismissedAlerts.update(set => new Set([...set, key]));
    if (this.message()?.alertKey === key) this.message.set(null);
  }

  markNoveltyRead(item: Novelty): void {
    const request = this.isSuperAdmin() && item.scope === 'platform' ? this.novelty.globalMarkRead(item.id) : this.novelty.markRead(item.id);
    this.novelties.update(list => list.filter(n => n.id !== item.id));
    request.subscribe({
      next: () => {
        if (this.isSuperAdmin() && item.scope === 'platform') { this.novelty.decrementGlobalUnread(); this.novelty.decrementGlobalCategory(item.category); }
        else { this.novelty.decrementBusinessUnread(); this.novelty.decrementBusinessCategory(item.category); }
      },
      error: () => this.refresh(true),
    });
  }

  // ------------------------------------------------------------ datos
  /** Al cambiar de cuenta (cerrar sesión y entrar con otra) se empieza de cero. */
  private ensureUser(): boolean {
    const uid = (this.auth.currentUser() as any)?.id ?? null;
    if (uid !== this.uid) {
      this.uid = uid;
      this.message.set(null); this.novelties.set([]); this.progress.set(null); this.panelOpen.set(false);
      this.greeted = false; this.noveltiesLoadedOnce = false; this.seenNovelties.clear(); this.shownAlerts.clear(); this.lastNoveltyLoad = 0; this.summaryAt = 0;
      this.dismissedAlerts.set(new Set());
      clearKuboGuide();
    }
    return !!uid;
  }

  /** Vuelve a calcular lo que Kubo sabe (novedades y avisos). */
  refresh(force = false): void {
    if (!this.ensureUser()) return;
    // Resumen liviano (espacios y pendientes); tras guardar se actualiza, como
    // mucho cada 20 s, para que los avisos no queden desactualizados.
    if (!this.summaryAt || (force && Date.now() - this.summaryAt > NOVELTY_MIN_GAP_MS)) {
      this.summaryAt = Date.now();
      this.dashboard.loadSummary(force);
    }
    if (!force && Date.now() - this.lastNoveltyLoad < NOVELTY_MIN_GAP_MS) return;
    this.lastNoveltyLoad = Date.now();
    const businessId = (this.auth.currentUser() as any)?.id || 0;
    const request = this.isSuperAdmin()
      ? this.novelty.globalList({ status: 'unread', page: 1, limit: 5 })
      : businessId ? this.novelty.list({ business_id: businessId, status: 'unread', page: 1, limit: 5 }) : null;
    if (!request) return;
    request.pipe(catchError(() => of(null))).subscribe(res => {
      if (!res) return;
      const items = res.data || [];
      if (this.isSuperAdmin()) this.novelty.setGlobalUnread(res.total || 0);
      else this.novelty.setBusinessUnread(res.total || 0);
      const fresh = this.noveltiesLoadedOnce ? items.filter(n => !this.seenNovelties.has(n.id)) : [];
      items.forEach(n => this.seenNovelties.add(n.id));
      this.novelties.set(items);
      const first = !this.noveltiesLoadedOnce;
      this.noveltiesLoadedOnce = true;
      if (first) this.greet(res.total || 0);
      else if (fresh.length && !this.message()) {
        const n = fresh[0];
        this.say({ title: fresh.length === 1 ? 'Tienes una novedad nueva' : `Tienes ${fresh.length} novedades nuevas`, text: n.title, tone: 'info', mood: 'explicando', action: { label: 'Ver novedades', run: () => this.openNovelties() }, ttl: 12 });
        this.react('saludo', 1800);
      }
    });
  }

  /** Primer saludo de la sesión. */
  private greet(unread: number): void {
    if (this.greeted) return;
    this.greeted = true;
    if (this.active() || this.suppressed()) return;
    const name = this.firstName();
    const hello = name ? `¡Hola, ${name}!` : '¡Hola!';
    if (unread > 0) {
      this.say({ title: hello, text: unread === 1 ? 'Tienes 1 novedad sin leer. Te la muestro cuando quieras.' : `Tienes ${unread} novedades sin leer. Te las muestro cuando quieras.`, tone: 'info', mood: 'saludo', action: { label: 'Ver novedades', run: () => this.openNovelties() }, ttl: 10 });
    } else {
      this.say({ title: `${hello} Soy Kubo`, text: 'Tu asistente de TuSala. Te guío paso a paso: tócame para ver avisos y novedades.', tone: 'info', mood: 'saludo', ttl: 7 });
    }
  }

  /** En cada cambio de módulo: recalcula y, si corresponde, recuerda lo importante. */
  routeChanged(): void {
    this.refresh();
    setTimeout(() => this.showNextAlert(), 1200);
  }

  /** Muestra el siguiente aviso importante aún no anunciado en la sesión. */
  showNextAlert(): void {
    if (this.message() || this.active() || this.suppressed() || this.panelOpen()) return;
    const now = Date.now();
    const alert = this.alerts().find(a => !this.shownAlerts.has(a.key))
      ?? this.alerts().find(a => a.insist && now - (this.shownAlerts.get(a.key) ?? 0) > INSIST_EVERY_MS);
    if (!alert) return;
    this.shownAlerts.set(alert.key, now);
    this.say({ title: alert.title, text: alert.message, tone: alert.tone, mood: alert.mood, alertKey: alert.key, action: alert.module && alert.actionLabel ? { label: alert.actionLabel, run: () => this.goModule(alert.module!, alert.target) } : null, ttl: 14 });
  }

  private read(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
  private write(key: string, value: string): void { try { localStorage.setItem(key, value); } catch { /* sin almacenamiento */ } }
}
